import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { Controller, useForm } from 'react-hook-form'
import SydModal from '../../../../../../commonDesign/SydModal'
import SydModalActions from '../../../../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../../../commonDesign/Button'
import SydLabel, { hookFormErrorAdapter } from '../../../../../../commonDesign/SydLabel'
import SydDatePicker from '../../../../../../commonDesign/SydDatePicker'
import { useRunClientStatusReportMutation } from '../../../../../../../api/groups'
import Alert from '../../../../../../atoms/Alert'
import { ALERT_SEVERITY, ALERT_VARIANT } from '../../../../../../../constants'

function RunNewReportModal ({
  open,
  onClose,
  levelId,
  statusTemplateId
}) {
  const [error, setError] = useState(null)

  const form = useForm({
    defaultValues: {
      statusTemplateId: null,
      asOfDate: dayjs().format('YYYY-MM-DD')
    }
  })

  useEffect(() => {
    form.setValue('statusTemplateId', statusTemplateId)
  }, [statusTemplateId, form])

  const { mutateAsync, isLoading } = useRunClientStatusReportMutation()

  const onSubmit = async (formData) => {
    try {
      setError(null)
      await mutateAsync({
        clientId: levelId,
        asOfDate: dayjs(formData.asOfDate).format('YYYY-MM-DD')
      })
      onClose?.()
    } catch (err) {
      console.error('Failed to run new report:', err)
      setError(err.message || 'Failed to create new report. Please try again.')
    }
  }

  return (
    <SydModal
      open={open}
      onClose={onClose}
      title='Run New Status Report'
      subtitle='Create a new active status report for this assignment'
    >
      <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col gap-4'>
        {error && (
          <Alert variant={ALERT_VARIANT.standard} severity={ALERT_SEVERITY.error}>
            {error}
          </Alert>
        )}

        <div className='flex flex-col gap-5'>
          <Controller
            control={form.control}
            name='asOfDate'
            rules={{
              required: 'Please select a date',
              validate: {
                notFuture: (value) => {
                  const selected = dayjs(value)
                  const today = dayjs()
                  return selected.isBefore(today) || selected.isSame(today, 'day') || 'Date cannot be in the future'
                }
              }
            }}
            render={({ field, fieldState }) => (
              <SydLabel
                label='As Of Date'
                description='The date for this status report'
                error={hookFormErrorAdapter(form, fieldState)}
              >
                <SydDatePicker
                  {...field}
                  size='sm'
                />
              </SydLabel>
            )}
          />
        </div>

        <SydModalActions>
          <SydButton
            variant='secondary'
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </SydButton>

          <SydButton
            priority='main'
            variant='primary'
            type='submit'
            disabled={isLoading}
            processing={isLoading}
          >
            {isLoading ? 'Creating...' : 'Run Report'}
          </SydButton>
        </SydModalActions>
      </form>
    </SydModal>
  )
}

RunNewReportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  statusTemplateId: PropTypes.number.isRequired,
  levelId: PropTypes.number.isRequired
}

export default RunNewReportModal
